import { useState } from 'react';
import { Check, X, Zap, Crown, Building2, Rocket, ArrowRight } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import type { SubscriptionTier, BillingCycle } from '../types/subscription';
import { 
  SUBSCRIPTION_PLANS, 
  getMonthlyPrice, 
  getAnnualPrice, 
  getAnnualSavingsPercentage 
} from '../data/subscriptionPlans';

export function PricingPage() {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const { currentTier, isLoading, startTrial, trialInfo } = useSubscription();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleSelectPlan = async (tier: SubscriptionTier) => {
    // Step 1: Check authentication
    if (!isAuthenticated) {
      // Redirect to login with plan selection
      navigate(`/login?plan=${tier}&returnUrl=/projects`);
      return;
    }
    
    // Step 2: Route to appropriate checkout/confirmation
    if (tier === 'free') {
      // Free tier - go directly to checkout confirmation
      navigate(`/checkout?plan=free`);
      return;
    }

    if (tier === 'enterprise') {
      // Enterprise - go to contact sales form
      navigate(`/checkout?plan=enterprise`);
      return;
    }

    // Step 3: Paid tiers - go to checkout
    navigate(`/checkout?plan=${tier}`);
  };

  const handleStartTrial = async () => {
    try {
      await startTrial();
      navigate('/projects?trial=started');
    } catch (error) {
      console.error('Trial start failed:', error);
      alert('Failed to start trial. Please try again.');
    }
  };

  const getPrice = (tier: SubscriptionTier) => {
    if (tier === 'free') return 0;
    if (tier === 'enterprise') return 'Custom';
    
    return billingCycle === 'monthly' 
      ? getMonthlyPrice(tier)
      : Math.round(getAnnualPrice(tier) / 12);
  };

  const getTierIcon = (tier: SubscriptionTier) => {
    switch (tier) {
      case 'free':
        return <Zap className="w-8 h-8" />;
      case 'starter':
        return <Crown className="w-8 h-8" />;
      case 'pro':
        return <Building2 className="w-8 h-8" />;
      case 'enterprise':
        return <Rocket className="w-8 h-8" />;
    }
  };

  const getTierColor = (tier: SubscriptionTier) => {
    switch (tier) {
      case 'free':
        return 'from-slate-500 to-slate-600';
      case 'starter':
        return 'from-blue-500 to-blue-600';
      case 'pro':
        return 'from-purple-500 to-purple-600';
      case 'enterprise':
        return 'from-orange-500 to-red-600';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 data-testid="page-h1-pricing" className="text-5xl md:text-6xl font-bold mb-6">
              Pricing
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Professional fire alarm design tools for every need
            </p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-full p-2">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-blue-600 shadow-lg'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                  billingCycle === 'annual'
                    ? 'bg-white text-blue-600 shadow-lg'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                Annual
                <span className="ml-2 px-2 py-1 bg-green-500 text-white text-xs rounded-full">
                  Save 17%
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="container mx-auto px-4 -mt-10 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {Object.values(SUBSCRIPTION_PLANS).map((plan) => {
            const price = getPrice(plan.id);
            const isCurrentPlan = currentTier === plan.id;
            const savingsPercent = billingCycle === 'annual' ? getAnnualSavingsPercentage(plan.id) : 0;

            return (
              <div
                key={plan.id}
                className={`bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-200 hover:scale-105 ${
                  plan.popular ? 'ring-4 ring-blue-500 relative' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 text-sm font-bold rounded-bl-lg">
                    MOST POPULAR
                  </div>
                )}

                <div className={`bg-gradient-to-r ${getTierColor(plan.id)} p-6 text-white`}>
                  <div className="flex items-center justify-between mb-4">
                    {getTierIcon(plan.id)}
                    {isCurrentPlan && (
                      <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold">
                        Current Plan
                      </span>
                    )}
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-sm opacity-90">{plan.description}</p>
                </div>

                <div className="p-6">
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      {typeof price === 'number' ? (
                        <>
                          <span className="text-4xl font-bold text-slate-900">${price}</span>
                          <span className="text-slate-600">/month</span>
                        </>
                      ) : (
                        <span className="text-4xl font-bold text-slate-900">{price}</span>
                      )}
                    </div>
                    {billingCycle === 'annual' && typeof price === 'number' && price > 0 && (
                      <p className="text-sm text-green-600 font-semibold mt-1">
                        Save {savingsPercent}% with annual billing
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={isCurrentPlan}
                    className={`w-full py-3 px-4 rounded-lg font-bold transition-all duration-200 mb-6 ${
                      isCurrentPlan
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : plan.popular
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white shadow-lg hover:shadow-xl'
                        : 'bg-slate-900 hover:bg-slate-800 text-white'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </span>
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : !isAuthenticated ? (
                      'Sign In to Select'
                    ) : plan.id === 'free' ? (
                      'Get Started'
                    ) : (
                      'Upgrade Now'
                    )}
                  </button>
                  
                  {!isAuthenticated && !isCurrentPlan && (
                    <p className="text-xs text-slate-500 text-center mb-4">
                      You'll be prompted to sign in before checkout
                    </p>
                  )}

                  <div className="space-y-3">
                    {Object.entries(plan.features)
                      .filter(([_, value]) => value === true || value === 'unlimited' || (typeof value === 'number' && value > 0))
                      .slice(0, 8)
                      .map(([key, value], idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-700">
                            {value === 'unlimited' ? `Unlimited ${key.replace(/([A-Z])/g, ' $1').trim()}` :
                             typeof value === 'number' ? `${value} ${key.replace(/([A-Z])/g, ' $1').trim()}` :
                             key.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                        </div>
                      ))}
                    {Object.entries(plan.features).filter(([_, v]) => v === true || v === 'unlimited' || (typeof v === 'number' && v > 0)).length > 8 && (
                      <p className="text-sm text-slate-500 italic">
                        +{Object.entries(plan.features).filter(([_, v]) => v === true || v === 'unlimited' || (typeof v === 'number' && v > 0)).length - 8} more features
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trial Banner */}
        {currentTier === 'free' && !trialInfo?.hasUsedTrial && (
          <div className="max-w-4xl mx-auto mt-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-8 text-white text-center shadow-2xl">
            <h3 className="text-3xl font-bold mb-4">Try Pro Free for 14 Days</h3>
            <p className="text-xl mb-6 text-orange-100">
              No credit card required. Full access to all Pro features.
            </p>
            <button
              onClick={handleStartTrial}
              disabled={isLoading}
              className="px-8 py-4 bg-white text-orange-600 font-bold text-lg rounded-lg hover:bg-orange-50 transition-colors shadow-lg hover:shadow-xl disabled:opacity-50"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 inline ml-2" />
            </button>
          </div>
        )}

        {/* Feature Comparison Table */}
        <div className="max-w-6xl mx-auto mt-20">
          <h2 className="text-3xl font-bold text-center mb-12">Feature Comparison</h2>
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="px-6 py-4 text-left font-bold text-slate-900">Feature</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-900">Starter</th>
                    <th className="px-6 py-4 text-center font-bold text-blue-600">Professional</th>
                    <th className="px-6 py-4 text-center font-bold text-purple-600">Business</th>
                    <th className="px-6 py-4 text-center font-bold text-orange-600">Enterprise</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="px-6 py-4 font-semibold">Active Projects</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-center text-blue-600 font-semibold">Unlimited</td>
                    <td className="px-6 py-4 text-center text-purple-600 font-semibold">Unlimited</td>
                    <td className="px-6 py-4 text-center text-orange-600 font-semibold">Unlimited</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-6 py-4 font-semibold">Device Database</td>
                    <td className="px-6 py-4 text-center">20 devices</td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-semibold">Loop Load Calculator</td>
                    <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-6 py-4 font-semibold">NFPA 72 References</td>
                    <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-semibold">Team Members</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-center">1</td>
                    <td className="px-6 py-4 text-center text-purple-600 font-semibold">5</td>
                    <td className="px-6 py-4 text-center text-orange-600 font-semibold">Unlimited</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-6 py-4 font-semibold">Custom Branding</td>
                    <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-semibold">API Access</td>
                    <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                    <td className="px-6 py-4 text-center">Basic</td>
                    <td className="px-6 py-4 text-center text-orange-600 font-semibold">Full</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-6 py-4 font-semibold">Support</td>
                    <td className="px-6 py-4 text-center">Community</td>
                    <td className="px-6 py-4 text-center">Email</td>
                    <td className="px-6 py-4 text-center">Priority</td>
                    <td className="px-6 py-4 text-center text-orange-600 font-semibold">24/7</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto mt-20">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[
              {
                q: 'Can I change plans at any time?',
                a: 'Yes! You can upgrade or downgrade your plan at any time. Upgrades take effect immediately, while downgrades take effect at the end of your current billing period.'
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit cards (Visa, Mastercard, Amex, Discover), PayPal, and ACH bank transfers for Business and Enterprise plans.'
              },
              {
                q: 'Is there a free trial?',
                a: 'Yes! We offer a 14-day free trial of our Professional plan with no credit card required. You get full access to all Pro features during the trial.'
              },
              {
                q: 'What happens if I exceed my plan limits?',
                a: "You'll receive a notification when approaching your limits. You can upgrade to a higher tier or wait until your next billing cycle resets your limits."
              },
              {
                q: 'Do you offer refunds?',
                a: 'Yes, we offer a 30-day money-back guarantee. If you\'re not satisfied with your subscription, contact us within 30 days for a full refund.'
              },
              {
                q: 'Can I get a discount for annual billing?',
                a: 'Yes! Annual billing saves you 17% compared to monthly billing. That\'s like getting 2 months free!'
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-bold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto mt-20 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Our team is here to help you choose the right plan
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@example.com"
              className="px-8 py-4 bg-white text-slate-900 font-bold rounded-lg hover:bg-slate-100 transition-colors"
            >
              Contact Sales
            </a>
            <a
              href="/demo"
              className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors"
            >
              Schedule a Demo
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
